import { BaseService } from './base';
import { ApiRequest } from '../lib/request';
import type { ApiResponse, ApiListResponse } from '../lib/response';
import { Product, ProductCategorised } from '../models/product';

export class ProductService extends BaseService<Product> {
  override model = Product;

  override modelName = 'product';
  override modelLabel = 'Products';

  /**
   * Filter products server-side and return the intersection of matching IDs
   * (AND across all active filters).
   *
   * Backend route `POST /product/filter` expects each filter as a
   * `{filter, params}` entry and returns one matched ID list per entry —
   * see `app/Models/Business/Filters/ProductFilter.php` for the keys
   * (`query`, `category`, `brand`, `supplier`). We transform the flat
   * `{key: params}` object the page hands us (the output of
   * `useFilterController.getFilterParams()`) into that envelope, intersect
   * the per-filter ID lists, and hand callers a single
   * `{data: {ids: number[]}}` shape ready for `listIDs(page)`.
   */
  async filter(filters: Record<string, any>): Promise<ApiResponse<{ ids: number[] }>> {
    const filterArray = Object.entries(filters).map(([filter, params]) => ({
      filter,
      params,
    }));

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/filter`,
      data: { filters: filterArray },
    });

    const response = await this.request(request);
    const results = (response.data?.filters ?? []) as Array<{ ids: number[] }>;

    let ids: number[] = [];
    if (results.length > 0) {
      const sets = results.map((r) => new Set(r.ids));
      const first = sets[0]!;
      ids = [...first].filter((id) => sets.every((s) => s.has(id)));
    }

    return { ...response, data: { ids } } as ApiResponse<{ ids: number[] }>;
  }

  async uncategorisedIDs(): Promise<number[]> {
    const response = await this.filter({
      category: { categories: [-1, 0] },
    });

    return response.data.ids;
  }

  async categorised(
    { modifiedSince }: { modifiedSince?: Date } = {},
  ): Promise<ApiListResponse<ProductCategorised>> {
    const headers: Record<string, string> = {};
    if (modifiedSince) {
      headers['If-Modified-Since'] = modifiedSince.toUTCString();
    }

    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/categorised`,
      headers,
    });

    return await this.requestList<ProductCategorised>(request, ProductCategorised);
  }

  async listIDs(
    ids: string[],
    page: number = 1,
  ): Promise<ApiListResponse<Product>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/ids`,
      data: { ids },
      query: { page: page.toString() },
    });

    return await this.requestList<Product>(request, Product);
  }

  async duplicate(productId: string): Promise<ApiResponse<Product>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${productId}/duplicate`,
    });

    return await this.requestItem<Product>(request, Product);
  }

  async adjustInventory(
    productId: string,
    { adjustment, type, otherLabel }: { adjustment: number; type: string; otherLabel?: string },
  ): Promise<ApiResponse<Product>> {
    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${productId}/adjustInventory`,
      data: { adjustment, type, otherLabel },
    });

    return await this.requestItem<Product>(request, Product);
  }

  async addPhoto(
    productId: string,
    image: File,
    options?: Record<string, any>,
  ): Promise<ApiResponse<Product>> {
    const formData = new FormData();
    formData.append('image', image);

    const request = new ApiRequest({
      method: 'POST',
      url: `${this.modelBaseUrl}/${productId}/addPhoto`,
      data: formData,
    });

    return await this.requestItem<Product>(request, Product);
  }

  async deletePhoto(productId: string): Promise<ApiResponse<Product>> {
    const request = new ApiRequest({
      method: 'GET',
      url: `${this.modelBaseUrl}/${productId}/deletePhoto`,
    });

    return await this.requestItem<Product>(request, Product);
  }
}
